import type { Schema } from '@dealdesk/api-schema';

export type PermissionCode = Schema<'PermissionCode'>;
export type PermissionScope = Schema<'PermissionScope'>;
export type Session = Schema<'Session'>;

/** Ownership attributes of a record, used to evaluate scoped permissions in the UI. */
export interface ScopedResource {
  ownerId?: string | null;
  teamId?: string | null;
  unitId?: string | null;
  branch?: string | null;
}

/** Every permission code in contract order. The type check below fails if the contract adds one. */
export const PERMISSION_CODES = [
  'deal.view', 'deal.create', 'deal.edit', 'deal.submit', 'deal.cancel', 'deal.financials.view',
  'document.upload', 'document.review', 'operations.update', 'bank.update',
  'customer.view', 'customer.create', 'customer.edit', 'customer.identity.edit', 'customer.pii.view',
  'collection.view', 'collection.manage', 'report.view', 'report.export',
  'config.view', 'config.edit', 'config.publish', 'admin.users', 'admin.roles', 'admin.audit',
  'deal.hold', 'deal.change_responsibility', 'settlement.manage', 'employer.view', 'employer.manage',
  'mis.view', 'import.run', 'org.manage',
  'staff_commission.view', 'staff_commission.manage', 'staff_commission.payout',
  'target.manage', 'import.alias.manage', 'deal.reassign', 'employer.enlistment.request', 'customer.import',
  'library.access', 'library.admin', 'performance.view',
] as const satisfies readonly PermissionCode[];
type MissingCodes = Exclude<PermissionCode, (typeof PERMISSION_CODES)[number]>;
type AssertNever<T extends never> = T;
export type _AllPermissionCodesListed = AssertNever<MissingCodes>;

const SCOPE_RANK: Record<PermissionScope, number> = { own: 0, team: 1, unit: 2, all: 3 };

/**
 * UI-only permission check. The backend is the authority; this only decides
 * whether to show an action.
 * - Without a resource: true if the user holds the permission at any scope.
 * - With a resource: true if the user's scope covers the resource.
 */
export function can(session: Session | null | undefined, code: PermissionCode, resource?: ScopedResource): boolean {
  if (!session) return false;
  const grant = session.permissions.find((p) => p.code === code);
  if (!grant) return false;
  if (!resource) return true;
  // Outsourced staff act on their own records only, whatever the role grants (backend enforces the same).
  if (session.user.employmentType === 'outsourced') return resource.ownerId == null || resource.ownerId === session.user.id;
  switch (grant.scope) {
    case 'all':
      return true;
    case 'unit':
      return resource.unitId == null || resource.unitId === session.user.unitId || resource.ownerId === session.user.id;
    case 'team':
      return resource.teamId == null || resource.teamId === session.user.teamId || resource.ownerId === session.user.id;
    case 'own':
      return resource.ownerId === session.user.id;
  }
}

export function scopeOf(session: Session | null | undefined, code: PermissionCode): PermissionScope | null {
  return session?.permissions.find((p) => p.code === code)?.scope ?? null;
}

/** The scope that applies to the user: the grant's scope, capped at own for outsourced staff (as the backend). */
export function effectiveScopeOf(session: Session | null | undefined, code: PermissionCode): PermissionScope | null {
  const scope = scopeOf(session, code);
  return scope && session?.user.employmentType === 'outsourced' ? 'own' : scope;
}

/**
 * My work (the shared queues) is hidden only for a normal salesperson: someone who sees just their own deals and picks
 * up no operations or finance work. Team leaders, sales managers and anyone with team / unit / all deal visibility keep
 * it, as do operations (operations.update) and finance (collection.manage). The rest work from My deals.
 */
export const MY_WORK_PERMISSIONS = ['operations.update', 'collection.manage'] as const satisfies readonly PermissionCode[];
export function hasMyWork(session: Session | null | undefined): boolean {
  if (!can(session, 'deal.view')) return false;
  if (MY_WORK_PERMISSIONS.some((c) => can(session, c))) return true;
  const scope = effectiveScopeOf(session, 'deal.view');
  return scope !== null && covers(scope, 'team');
}

/**
 * "My commission": the user sees only their own staff commission (staff_commission.view at own — or outsourced — and no
 * staff_commission.manage). The area is then one personal page; rules, payout runs and other people are not shown.
 */
export function seesOwnCommissionOnly(session: Session | null | undefined): boolean {
  return effectiveScopeOf(session, 'staff_commission.view') === 'own' && !can(session, 'staff_commission.manage');
}

export function covers(scope: PermissionScope, required: PermissionScope): boolean {
  return SCOPE_RANK[scope] >= SCOPE_RANK[required];
}
